import { Typography } from '@mui/material';
import React, { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UseQueryResult } from 'react-query';

import {
  CrudTable,
  DataGridColumnDefinition,
  StylizedNumber,
} from 'components';
import { TranslationNamespace } from 'i18n';
import { PayinCancelReasonRow, PayinCancelReasons } from 'types';
import { orderUtils, payinProviderStatisticsUtils } from 'utils';

type Props = {
  queryResult: UseQueryResult<PayinCancelReasons>;
};

export const CancelReasonsTable: React.FC<Props> = ({ queryResult }) => {
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.statistics.provider_conversion.cancel_reasons',
  });

  const rowsQueryResult = useMemo(
    () =>
      ({
        ...queryResult,
        data: queryResult.data?.items,
      } as UseQueryResult<PayinCancelReasonRow[]>),
    [queryResult],
  );

  const scopeHint = useMemo(() => {
    const scope = queryResult.data?.providerScope;
    return scope ? payinProviderStatisticsUtils.getScopeHint(scope) : null;
  }, [queryResult.data]);

  // Without a provider filter the block spans every cancelled order, including the ones that never
  // reached a P2P provider — say so instead of letting the numbers imply otherwise
  const scopeSummary = useMemo(() => {
    const data = queryResult.data;
    if (!data?.totalCancelledCount) {
      return null;
    }
    return t('summary', {
      total: data.totalCancelledCount,
      assigned: data.assignedToProviderCount,
      notAssigned: data.notAssignedCount,
    });
  }, [queryResult.data, t]);

  const columns = useMemo(
    (): DataGridColumnDefinition<PayinCancelReasonRow>[] => [
      {
        header: t('columns.reason'),
        valueGetter: (item) =>
          item.reason
            ? orderUtils.getCancelOrderReason(item.reason)
            : t('unknown_reason'),
      },
      {
        header: t('columns.count'),
        valueGetter: (item) => (
          <StylizedNumber value={item.count} unit={tCommon('units.items')} />
        ),
      },
      {
        header: t('columns.share'),
        valueGetter: (item) => <StylizedNumber value={item.share} unit="%" />,
      },
      {
        header: t('columns.median_lifetime'),
        valueGetter: (item) => (
          <StylizedNumber
            value={item.medianLifetimeMinutes}
            unit={tCommon('units.minutes')}
          />
        ),
      },
      {
        header: t('columns.p90_lifetime'),
        valueGetter: (item) => (
          <StylizedNumber
            value={item.p90LifetimeMinutes}
            unit={tCommon('units.minutes')}
          />
        ),
      },
    ],
    [t, tCommon],
  );

  return (
    <Fragment>
      <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
        {t('hint')}
      </Typography>
      {!!scopeHint && (
        <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
          {scopeHint}
        </Typography>
      )}
      {!!scopeSummary && (
        <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
          {scopeSummary}
        </Typography>
      )}
      <CrudTable queryResult={rowsQueryResult} columns={columns} />
    </Fragment>
  );
};
